import { useEffect, useRef } from 'react';
import PageBack from '../components/PageBack';
import { getBlogPost, getBlogRelated, isPreviewRender } from '../../../shared-web/site-content-runtime';
import { hydrateCtaLinks } from '../../../shared-web/cta-targets';
import DraftPreviewBanner from '../components/DraftPreviewBanner';
import AuthoredPage from '../components/AuthoredPage';

/**
 * Which site chrome an authored page asks for.
 *
 * Mirrors CHROME_DARK_MARKER / chromeThemeFor in
 * src/edge/content/page-html.js. Duplicated rather than imported because the
 * marketing bundle must not pull an edge module into the browser; pinned by a
 * test on the edge side.
 */
function chromeThemeFor(html?: string): 'light' | 'dark' {
  return String(html || '').includes('data-ember-chrome="dark"') ? 'dark' : 'light';
}

import { PostMeta, formatDate } from './BlogIndex';

/**
 * /blog/<slug> — one article.
 *
 * The body is injected as HTML, rendered from markdown on the edge by a parser
 * that cannot emit raw HTML or an executable URL (src/edge/content/markdown.js).
 * Its `ember:` links arrive as working hrefs with no attribution; hydrateCtaLinks
 * upgrades them once the client can compute it.
 */
export default function BlogPost() {
  const post = getBlogPost();
  const related = getBlogRelated();
  const preview = isPreviewRender();
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    hydrateCtaLinks(bodyRef.current);
  }, [post?.slug, post?.body_html]);

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f9f5e9] text-ink" style={BODY}>
        <div className="eh-page-top mx-auto w-full max-w-3xl px-6 sm:px-10">
          <PageBack fallback="/blog" />
        </div>
        <div
          className="mx-auto w-full max-w-3xl flex-1 px-6 py-24 text-center sm:px-10"
          data-log-seen="page_failure"
          data-log-state="not_here"
        >
          <h1 className="mb-4 text-3xl font-bold" style={HEADING}>This article is not here</h1>
          <p className="mb-8 text-ink/80">It may have been retired, or the link may be wrong.</p>
          <a className="text-oriOrange underline" href="/blog">Read the blog</a>
        </div>
      </div>
    );
  }

  // The article brought its own layout — see the note in UseCasePage.
  if (post.page_html) {
    // See the note in UseCasePage: the article's CSS is unconditional, the
    // chrome otherwise follows the visitor's OS.
    const chrome = chromeThemeFor(post.page_html);
    return (
      <div className={`flex min-h-screen flex-col text-ink ${chrome === 'dark' ? 'bg-[#14110e]' : 'bg-[#f9f5e9]'}`} style={BODY}>
        {preview && <DraftPreviewBanner />}
        <div className="eh-page-top mx-auto w-full max-w-3xl px-6 sm:px-10">
          <PageBack fallback="/blog" theme={chrome} />
        </div>
        <AuthoredPage html={post.page_html} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f9f5e9] text-ink" style={BODY}>
      {preview && <DraftPreviewBanner />}

      <article className="eh-page-top mx-auto w-full max-w-3xl flex-1 px-6 pb-16 sm:px-10">
        <div className="eh-page-back-row">
          <PageBack fallback="/blog" />
        </div>
        {/* The visible half of the BreadcrumbList the edge emits. Schema
            claiming a trail the page does not show is discounted, so the two
            ship together — including the category step. */}
        <nav className="mb-6 text-sm text-ink/60" aria-label="Breadcrumb">
          <a className="hover:underline" href="/">Ember</a>
          <span className="mx-2" aria-hidden="true">/</span>
          <a className="hover:underline" href="/blog">Blog</a>
          {post.category_slug && (
            <>
              <span className="mx-2" aria-hidden="true">/</span>
              <a className="hover:underline" href={`/blog/category/${post.category_slug}`}>
                {post.category_name}
              </a>
            </>
          )}
        </nav>

        <header className="mb-8">
          <h1 className="mb-4 text-4xl font-bold tracking-tight" style={HEADING}>{post.title}</h1>
          <PostMeta post={post} />
          {/* Shown only when it differs from the publication date: "Updated" on
              an article edited an hour after publishing is noise, and it makes
              the label mean nothing on the articles where it matters. */}
          {post.updated_at && !sameDay(post.updated_at, post.published_at) && (
            <p className="mt-1 text-xs text-ink/50" style={BODY}>
              Updated <time dateTime={post.updated_at}>{formatDate(post.updated_at)}</time>
            </p>
          )}
        </header>

        {post.image_url && (
          <img
            className="mb-10 w-full rounded-xl object-cover"
            src={post.image_url}
            alt={`${post.title} — article illustration`}
          />
        )}

        <div
          ref={bodyRef}
          className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-ink/20 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:font-semibold [&_img]:rounded-lg [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-ink/5 [&_pre]:p-4 [&_ul]:list-disc [&_ul]:pl-6"
          style={BODY}
          // Safe by construction — see the component header.
          dangerouslySetInnerHTML={{ __html: post.body_html || '' }}
        />

        <section className="mt-14 rounded-xl bg-white/60 p-8 text-center">
          <h2 className="mb-3 text-2xl font-bold" style={HEADING}>You can start right now</h2>
          <p className="mb-6 text-ink/80" style={BODY}>
            No account to begin. Say as much or as little as you want.
          </p>
          {/* Resolved by hydrateCtaLinks like the ones in the body: same
              destination table, same attribution, one place to change it.

              The href is the no-JS and crawler-visible fallback, so it must
              stay equal to CTA_TARGETS.try.path in
              src/edge/content/cta-targets.js — a test pins the two together.
              It said /collect?trial=1 until 2026-09-06, three weeks after the
              named destination had moved, which meant every reader with the
              bundle blocked and every crawler reading this page was sent to
              the retired trial door while the hydrated link went to /talk. */}
          <a
            className="eh-primary-button"
            href="/talk"
            data-ember-cta="try"
            data-log="blog_post_cta_try"
            data-log-seen="blog_post_cta_try"
          >
            <span>Talk to Ember</span>
          </a>
        </section>

        {related.length > 0 && (
          <nav className="mt-14 border-t border-ink/10 pt-8" aria-label="Related reading">
            <h2 className="mb-4 text-lg font-semibold" style={HEADING}>More in {post.category_name}</h2>
            <ul className="space-y-3">
              {related.map((other) => (
                <li key={other.slug}>
                  <a
                    className="text-oriOrange underline"
                    href={`/blog/${other.slug}`}
                    data-log={`blog_related_${other.slug}`}
                    data-log-seen={`blog_related_${other.slug}`}
                  >
                    {other.title}
                  </a>
                  {other.excerpt && (
                    <p className="mt-1 text-sm text-ink/70" style={BODY}>{other.excerpt}</p>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        )}

        <p className="mt-10 text-sm">
          <a className="text-oriOrange underline" href="/blog" data-log="blog_post_back">
            Read more from Ember
          </a>
        </p>
      </article>
    </div>
  );
}

function sameDay(a: string, b: string): boolean {
  if (!a || !b) return false;
  try {
    return new Date(a).toDateString() === new Date(b).toDateString();
  } catch {
    return false;
  }
}

/* The SPA's --eh-sans stack (EmberApp.css) verbatim, so blog type reads as
   the same family as /requests. The marketing bundle has no --eh-* tokens. */
const EH_SANS = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans CJK SC", "Nirmala UI", "Geeza Pro", sans-serif';

// one title face across the site (2026-09-06 走查): headings speak, so the display serif
const EH_SERIF = '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Palatino, "Hoefler Text", Georgia, serif';
const HEADING = {
  fontFamily: EH_SERIF,
  fontWeight: 400,
  letterSpacing: '-0.015em',
  color: '#3A331D',
} as const;

const BODY = {
  fontFamily: EH_SANS,
} as const;
